import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CircularProgress,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SendIcon from "@mui/icons-material/Send";
import DriveFileMoveOutlinedIcon from "@mui/icons-material/DriveFileMoveOutlined";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PeopleIcon from "@mui/icons-material/People";
import { Link as RouterLink } from "react-router-dom";
import Moment from "moment";
import { toast } from "react-toastify";
import { CollectionsApi } from "../../services/api/api";
import { confirm } from "../confirmation";
import { FolderListRow } from "./collectionFolderItem";

function CollectionListRow({ collection, onSend, onMenuOpen, isUploader }) {
  return (
    <TableRow hover data-collection-id={collection.collectionId || collection.relId} data-linked={collection.isLinked === true}>
      <TableCell sx={{ width: 32, p: 0, pl: 0.5 }}>
        <Box
          draggable
          role="button"
          aria-label="Drag to move collection"
          tabIndex={0}
          onDragStart={(e) => {
            e.dataTransfer.setData('text/plain', JSON.stringify({
              collectionId: collection.collectionId || collection.relId,
              title: collection.title
            }));
            e.dataTransfer.effectAllowed = 'move';
            const tr = e.target.closest('tr');
            if (tr) {
              e.dataTransfer.setDragImage(tr, tr.offsetWidth / 2, 20);
              tr.style.opacity = '0.4';
            }
          }}
          onDragEnd={(e) => {
            const tr = e.target.closest('tr');
            if (tr) tr.style.opacity = '1';
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onMenuOpen?.(e, collection);
            }
          }}
          sx={{ cursor: 'grab', display: 'inline-flex' }}
        >
          <DragIndicatorIcon fontSize="small" aria-hidden="true" sx={{ color: 'text.secondary', opacity: 0.5, '&:hover': { opacity: 1 } }} />
        </Box>
      </TableCell>
      <TableCell>
        <RouterLink to={`/collections/${collection.relId}`} style={{ textDecoration: 'none', color: 'inherit' }}>
          {collection.previewImage ? (
            <Avatar
              variant="rounded"
              src={collection.previewImage}
              alt={collection.title}
              sx={{ width: 40, height: 40 }}
            />
          ) : (
            <Avatar variant="rounded" sx={{ width: 40, height: 40, bgcolor: "grey.300" }} alt={collection.title}>
              <Typography variant="caption">No</Typography>
            </Avatar>
          )}
        </RouterLink>
      </TableCell>
      <TableCell>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <RouterLink to={`/collections/${collection.relId}`} style={{ textDecoration: 'none', color: 'inherit' }}>
            <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
              {collection.title}
            </Typography>
          </RouterLink>
          <Chip
            label={collection.tierName}
            size="small"
            sx={{
              backgroundColor: collection.tierColor,
              color: collection.fontColor,
              fontSize: "0.7rem",
              height: 20,
            }}
          />
          {collection.isLinked === true && (
            <Chip
              label={`Linked: ${collection.linkType || "shared"}`}
              size="small"
              color="secondary"
              variant="outlined"
              sx={{ height: 20, fontSize: "0.7rem" }}
            />
          )}
        </Box>
      </TableCell>
      <TableCell>
        {collection.folderPath && collection.folderPath.length > 0 ? (
          <Chip
            size="small"
            variant="outlined"
            label={collection.folderPath.join(" / ")}
            sx={{ fontSize: "0.7rem" }}
          />
        ) : (
          <Typography variant="body2" color="text.secondary">
            </Typography>
        )}
      </TableCell>
      <TableCell>
        <Typography variant="body2" color="text.secondary">
          {Moment.unix(collection.date).format("MMM D, YYYY")}
        </Typography>
      </TableCell>
      <TableCell align="right">
        {!isUploader && (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onSend(collection);
            }}
            aria-label="Send collection"
          >
            <SendIcon fontSize="small" />
          </IconButton>
        )}
        <IconButton
          size="small"
          onClick={(e) => onMenuOpen(e, collection)}
          aria-label="More actions"
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </TableCell>
    </TableRow>
  );
}

function CollectionListView({ collections, AWS, userId, openModal, token, accountType, onMove, subfolders, onFolderOpen, onDropIntoFolder, onRenamed, onDeleted, onManageAccess, onRemoveFromLibrary }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [overFolder, setOverFolder] = useState(null);
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameValue, setRenameValue] = useState("");
  const [renameLoading, setRenameLoading] = useState(false);

  const handleMenuOpen = (event, collection) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedCollection(collection);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedCollection(null);
  };

  const handleMove = () => {
    handleMenuClose();
    if (selectedCollection && onMove) {
      onMove(selectedCollection);
    }
  };

  const handleManageAccess = () => {
    handleMenuClose();
    if (selectedCollection && onManageAccess) {
      onManageAccess(selectedCollection);
    }
  };

  const handleRemoveFromLibrary = () => {
    handleMenuClose();
    if (selectedCollection && onRemoveFromLibrary) {
      onRemoveFromLibrary(selectedCollection);
    }
  };

  const handleSend = (collection) => {
    handleMenuClose();
    openModal(collection.relId, collection.title);
  };

  const openRename = () => {
    if (!selectedCollection) return;
    setRenameValue(selectedCollection.title || "");
    handleMenuClose();
    setRenameOpen(true);
  };

  const handleRenameSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const trimmed = (renameValue || "").trim();
    if (!trimmed) {
      toast.warn("Name cannot be empty");
      return;
    }
    if (!selectedCollection || !selectedCollection.collectionId) {
      toast.error("Cannot rename: missing collectionId");
      return;
    }
    setRenameLoading(true);
    try {
      const t = typeof token === 'function' ? await token() : token;
      const result = await CollectionsApi.updateCollectionName(t, selectedCollection.collectionId, trimmed);
      if (result && result.success !== false) {
        toast.success("Collection renamed");
        setRenameOpen(false);
        if (typeof onRenamed === 'function') {
          onRenamed();
        }
      } else {
        toast.error("Rename failed: " + ((result && result.message) || "Unknown error"));
      }
    } catch (err) {
      toast.error("Rename failed: " + (err && err.message ? err.message : String(err)));
    }
    setRenameLoading(false);
  };

  const handleDelete = () => {
    if (!selectedCollection || !selectedCollection.collectionId) {
      toast.error("Cannot delete: missing collectionId");
      return;
    }
    handleMenuClose();
    confirm(
      "Are you sure you want to delete this collection? This cannot be undone.",
      "Delete",
      "Cancel"
    ).then(async (ok) => {
      if (!ok) return;
      try {
        const t = typeof token === 'function' ? await token() : token;
        const result = await CollectionsApi.deleteCollection(t, selectedCollection.collectionId);
        if (result && result.success !== false) {
          toast.success("Collection deleted");
          if (typeof onDeleted === 'function') {
            onDeleted();
          }
        } else {
          toast.error("Delete failed: " + ((result && result.message) || "Unknown error"));
        }
      } catch (err) {
        toast.error("Delete failed: " + (err && err.message ? err.message : String(err)));
      }
    }, () => {});
  };

  return (
    <Box>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 32 }} />
              <TableCell sx={{ width: 50 }} />
              <TableCell>Name</TableCell>
              <TableCell>Folder</TableCell>
              <TableCell>Created</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {/* Folder rows */}
            {subfolders?.map((folder) => (
              <TableRow key={`folder-${folder.name}`} hover data-testid="folder-row">
                <TableCell colSpan={6} sx={{ p: 0 }}>
                  <FolderListRow
                    folder={folder}
                    onOpen={() => onFolderOpen?.(folder.path)}
                    isDropTarget={overFolder === folder.name}
                    onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setOverFolder(folder.name); }}
                    onDragLeave={() => setOverFolder(null)}
                    onDropCollection={(e) => {
                      e.preventDefault();
                      try {
                        const d = JSON.parse(e.dataTransfer.getData('text/plain'));
                        if (d.collectionId) {
                          onDropIntoFolder?.(d.collectionId, folder.path);
                        }
                      } catch {}
                      setOverFolder(null);
                    }}
                  />
                </TableCell>
              </TableRow>
            ))}
            {collections.map((collection) => (
              <CollectionListRow
                key={collection.relId}
                collection={collection}
                isUploader={accountType === "uploader"}
                onSend={handleSend}
                onMenuOpen={handleMenuOpen}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {selectedCollection?.isLinked === true ? (
          <MenuItem
            onClick={(e) => {
              e.stopPropagation();
              handleRemoveFromLibrary();
            }}
          >
            <ListItemIcon>
              <DeleteIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Remove from My Library</ListItemText>
          </MenuItem>
        ) : [
          <MenuItem key="manage" onClick={handleManageAccess}>
            <ListItemIcon><PeopleIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Manage Access</ListItemText>
          </MenuItem>,
          <MenuItem key="move" onClick={handleMove}>
            <ListItemIcon>
              <DriveFileMoveOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Move to folder</ListItemText>
          </MenuItem>,
          <MenuItem key="rename" onClick={openRename}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Rename</ListItemText>
          </MenuItem>,
          <MenuItem key="delete" onClick={handleDelete}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Delete</ListItemText>
          </MenuItem>
        ]}
      </Menu>
      <Dialog open={renameOpen} onClose={() => { if (!renameLoading) setRenameOpen(false); }} fullWidth maxWidth="sm">
        <DialogTitle>Rename Collection</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Collection name"
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            margin="normal"
            disabled={renameLoading}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameOpen(false)} disabled={renameLoading}>Cancel</Button>
          <Button onClick={handleRenameSubmit} variant="contained" disabled={renameLoading}>
            {renameLoading ? <CircularProgress size={20} /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default CollectionListView;
